import { Box, Chip, FormControl, Input, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { services } from "api/serviceConfig";
import CustomerIdRequest from "api/sharedModels/CustomerIdRequest";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { LynxButton } from "components/LynxButton/LynxButton";
import { LynxInput } from "components/LynxInput/LynxInput";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStore } from "store/StoreConfigs";
import { FormProps } from "./FormProps";
import { formStyles } from "./FormStyles";

export default observer(function AddCustomerForm(props: FormProps) {
    const classes = formStyles();
    const { identityStore, customerStore } = useStore();

    const [fields, setFields] = useState({
        name: "",
        email: "",
        address: "",
        modules: [],
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | { name?: string | undefined; value: unknown }>
    ) => {
        setFields({ ...fields, [e.target.name!]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (loading) {
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            const response = await services.UserManagement.addCustomer({
                ...fields,
                customerId: identityStore.currentCustomerId,
            });

            if (response.status !== 200) {
                if (response.status === 400 && response.data.title === "One or more validation errors occurred.") {
                    setErrors(Object.entries(response.data.errors).flatMap((x) => x[1]) as string[]);
                } else if (response.status === 400 && response.data.errorMessage) {
                    setErrors([response.data.errorMessage]);
                } else {
                    console.log(response);
                    setErrors(["Unknown error occured. Check console output"]);
                }
            } else {
                props.successCallback(fields.name);
            }
        } catch (error) {
            console.log(error);
            setErrors(["Unknown error occured. Check console output."]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const request: CustomerIdRequest = {
            customerId: identityStore.currentCustomerId,
        };

        customerStore.loadModules(request);
    }, []);

    if (customerStore.areModulesLoading) {
        return <LoadingIndicator className={classes.loadingIndicator} />;
    }

    return (
        <Box padding={2}>
            <Typography variant="h3" gutterBottom>
                Add Customer
            </Typography>
            <form className={classes.form} onSubmit={handleSubmit}>
                <LynxInput
                    size="large"
                    name="name"
                    value={fields.name}
                    label="Customer Name"
                    onChange={handleInputChange}
                    placeholder="e.g. Carrier"
                    formControlClassName={classes.input}
                />
                <LynxInput
                    size="large"
                    name="email"
                    value={fields.email}
                    label="Email"
                    onChange={handleInputChange}
                    placeholder="support@carrier.com"
                    formControlClassName={classes.input}
                />
                <LynxInput
                    size="large"
                    name="address"
                    value={fields.address}
                    label="Address"
                    onChange={handleInputChange}
                    formControlClassName={classes.input}
                />
                <FormControl className={classes.input}>
                    <InputLabel>Modules</InputLabel>
                    <Select
                        name="modules"
                        multiple
                        value={fields.modules}
                        onChange={handleInputChange}
                        input={<Input id="select-multiple-chip" />}
                        renderValue={(selected: any) => (
                            <div>
                                {selected.map((value: any) => (
                                    <Chip
                                        key={value.id}
                                        label={customerStore.modules.find((x) => x.id === value)?.name}
                                        className={classes.selectChip}
                                    />
                                ))}
                            </div>
                        )}
                    >
                        {customerStore.modules.map((x) => (
                            <MenuItem key={x.id} value={x.id}>
                                {x.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <LynxButton
                    className={classes.submitButton}
                    size="large"
                    responsive={false}
                    type="submit"
                    loading={loading}
                    disabled={loading}
                >
                    Submit
                </LynxButton>
                {errors.length > 0 &&
                    errors.map((x) => (
                        <Typography key={x} variant="body1" className={classes.error}>
                            {x}
                        </Typography>
                    ))}
            </form>
        </Box>
    );
});
